import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

// material-ui
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';

// project imports
import Avatar from 'components/@extended/Avatar';
import useAuth from 'hooks/useAuth';
import useOnlineStatus from 'hooks/useOnlineStatus';
import { useMyProfile } from 'hooks/api/useUsers';
import { useGetMenuMaster } from 'api/menu';

// assets
import RightOutlined from '@ant-design/icons/RightOutlined';
import { DEFAULT_AVATAR } from 'utils/avatarDefaults';

const ExpandMore = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'theme' && prop !== 'expand' && prop !== 'drawerOpen'
})(({ theme }) => ({
  transform: 'rotate(-90deg)',
  marginLeft: 'auto',
  color: theme.palette.secondary.dark,
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest
  }),
  variants: [
    {
      props: ({ expand }) => !expand,
      style: { transform: 'rotate(0deg)' }
    },
    {
      props: ({ drawerOpen }) => !drawerOpen,
      style: { opacity: 0, width: 50, height: 50 }
    }
  ]
}));

// ==============================|| DRAWER - USER ||============================== //

export default function NavUser() {
  const navigate = useNavigate();
  const intl = useIntl();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const { logout, user } = useAuth();
  const { offlineUser } = useOnlineStatus();

  // Use online user or fallback to offline cached user
  const activeUser = user || offlineUser;

  const { data: profile } = useMyProfile();
  const avatarSrc = profile?.profile_image_url || DEFAULT_AVATAR;

  const handleLogout = async () => {
    // Always clear local auth state so AuthGuard can react, even if the network
    // is flapping or the backend logout 401s on an expired token (handled inside
    // JWTContext.logout).
    try {
      if (logout) await logout();
    } catch (err) {
      console.warn('[NavUser] logout threw:', err);
    }
    try {
      navigate('/login', { replace: true, state: { from: '' } });
    } catch (navErr) {
      console.warn('[NavUser] navigate to /login failed, hard-redirecting:', navErr);
      window.location.replace('/login');
    }
  };

  // Get display name and role title. Prefer the self-service profile name so the
  // drawer reflects edits immediately (the auth-context user isn't refetched on
  // profile save); fall back to the auth-context name and existing defaults.
  const profileName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ');
  const displayName =
    profileName ||
    (activeUser?.role === 'therapist' && (activeUser?.name === 'JWT User' || !activeUser?.name)
      ? 'Maya'
      : activeUser?.name || activeUser?.firstName || 'User');

  const getRoleTitle = (role) => {
    switch (role) {
      case 'supervisor':
        return intl.formatMessage({ id: 'profile.role.supervisor' });
      case 'therapist':
        return intl.formatMessage({ id: 'profile.role.therapist' });
      default:
        return intl.formatMessage({ id: 'profile.role.default' });
    }
  };
  const roleTitle = getRoleTitle(activeUser?.role);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ p: 1.25, px: !drawerOpen ? 1.25 : 3, borderTop: '2px solid', borderTopColor: 'divider' }}>
      <List disablePadding>
        <ListItem
          disablePadding
          secondaryAction={
            <ExpandMore
              expand={open}
              drawerOpen={drawerOpen}
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              aria-label="show more"
            >
              <RightOutlined style={{ fontSize: '0.625rem' }} />
            </ExpandMore>
          }
          sx={{ '& .MuiListItemSecondaryAction-root': { right: !drawerOpen ? -20 : -16 } }}
        >
          <ListItemAvatar>
            <Avatar alt="Avatar" src={avatarSrc} sx={{ ...(drawerOpen && { width: 46, height: 46 }) }} />
          </ListItemAvatar>
          <ListItemText primary={displayName} secondary={roleTitle} />
        </ListItem>
      </List>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ list: { 'aria-labelledby': 'basic-button' } }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem onClick={handleLogout}>{intl.formatMessage({ id: 'profile.logout' })}</MenuItem>
        <MenuItem component={Link} to="/apps/profiles/user/personal" onClick={handleClose}>
          {intl.formatMessage({ id: 'profile.profileTab' })}
        </MenuItem>
        <MenuItem component={Link} to="/apps/profiles/account/my-account" onClick={handleClose}>
          {intl.formatMessage({ id: 'navUser.myAccount' })}
        </MenuItem>
      </Menu>
    </Box>
  );
}
